/**
 * @fileoverview Edit User dialog.
 * @description Edits a user through PATCH /users/{userId}: first, middle and last name, the mobile
 * number, and — for an ACTIVE user only — the primary Sales role and reporting manager, whose
 * choices come from GET /users/{userId}/edit-options (the managers re-load for the chosen role, and
 * changing the role clears the manager). Employee ID, email, department and date of joining are
 * shown read-only; the backend doesn't accept them. The user's State (or All India, which clears
 * it) can be changed for any user once the choices, from the Tenants prospect metadata, have
 * loaded; while they load or if they fail the State picker is disabled. Only changed fields are
 * sent, and saving with no change just closes. The update carries the ETag of the details the form
 * was filled from as If-Match: if someone else changed the user meanwhile (412 / 3022), the
 * details are re-read and the form refills with them, keeping the message so the editor knows to
 * review and save again. A "recent sign-in required" refusal (403 / 2009, which edit-options can
 * also return) offers Sign in again. The page mounts this only while a user is being edited, so
 * every open starts clean (see the modal-lifecycle rule). The data and the command are in
 * useEditUserDialog; closing is held while a save is in flight.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import EditUserForm from '@/features/users/components/EditUserForm'
import UserRequestError from '@/features/users/components/UserRequestError'
import { EDIT_FORM_ID } from '@/features/users/constants/lifecycle.constants'
import { useEditUserDialog } from '@/features/users/hooks/useEditUserDialog'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import Modal from '@/shared/components/molecules/Modal'

import type { UserDetail } from '@/features/users/types/users.types'

interface EditUserModalProps {
  userId: string
  onClose: () => void
  onUpdated: (user: UserDetail) => void
}

/**
 * @component
 * @param {EditUserModalProps} props - The user to edit, and the dialog's callbacks.
 * @param {string} props.userId - The user to edit.
 * @param {() => void} props.onClose - Dismisses the dialog without saving.
 * @param {(user: UserDetail) => void} props.onUpdated - Called with the updated user after a
 * successful save, before the dialog closes.
 */
function EditUserModal({ userId, onClose, onUpdated }: EditUserModalProps) {
  const { t } = useTranslation()
  const dialog = useEditUserDialog({ userId, onClose, onUpdated })
  const { detail } = dialog

  return (
    <Modal
      data-testid="edit-user-modal"
      open
      onClose={dialog.close}
      title={t('sales.userManagement.editUserForm.title')}
      subtitle={
        detail
          ? t('sales.userManagement.editUserForm.subtitle', { name: detail.user.displayName })
          : ''
      }
      size="lg"
      footer={
        <>
          <ModalFooterButton
            type="button"
            onClick={dialog.close}
            disabled={dialog.isSaving}
            data-testid="edit-user-cancel-button"
          >
            {t('sales.userManagement.editUserForm.cancel')}
          </ModalFooterButton>
          <ModalFooterButton
            type="submit"
            variant="primary"
            form={EDIT_FORM_ID}
            data-testid="edit-user-submit-button"
            isLoading={dialog.isSaving}
            disabled={!detail}
          >
            {t('sales.userManagement.editUserForm.submit')}
          </ModalFooterButton>
        </>
      }
    >
      {dialog.isLoading && (
        <p className="text-[13px] text-slate" data-testid="edit-user-loading">
          {t('sales.userManagement.viewUser.loading')}
        </p>
      )}

      {dialog.loadError && <UserRequestError error={dialog.loadError} testIdPrefix="edit-user" />}

      {detail && (
        <div className="flex flex-col gap-4">
          {/* Keyed by version, so re-read details after a conflict refill the form. */}
          <EditUserForm key={detail.etag} user={detail.user} onSubmit={dialog.submit} />
          {dialog.saveError && (
            <UserRequestError error={dialog.saveError} testIdPrefix="edit-user" />
          )}
        </div>
      )}
    </Modal>
  )
}

export default EditUserModal
